<template>
  <div class="form__input">
    <label
      v-if="label"
      :class="{ 'label--required': required }"
      class="label label--muted label--semibold"
    >
      {{ label }}
    </label>
    <div class="phone" :class="{ 'phone--errors': allErrors?.length }" ref="phoneRef">
      <div class="phone__country">
        <button class="phone__country-select" type="button" @click="isOpenDropdown = true" :disabled="disabled">
          {{ selectedCountry.flag }} ↓ {{ selectedCountry.number_code }}
        </button>
        <t-dropdown
          class="phone__dropdown"
          v-model="isOpenDropdown"
          :parent="phoneRef"
          :items="countries"
          :selectedItem="selectedCountry"
          :output="dropdownOutput"
          @select="onSelectOption"
        />
      </div>
      <input
        class="phone__field"
        type="tel"
        :value="localPhone"
        :placeholder="disabled ? null : placeholder"
        :disabled="disabled"
        @input="onInput"
      >
    </div>
    <t-errors :errors="allErrors" />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import parsePhoneNumber from 'libphonenumber-js/min'
import { TDropdown, TErrors } from '@shared/ui'
import { useFieldErrors } from '@shared/lib'

const props = defineProps({
  label: String,
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Введите номер' },
  disabled: Boolean,
  errors: Array,
  countryCode: String,
  name: String,
})

const { fieldErrors, clearFieldErrors } = useFieldErrors(props.name)
const allErrors = computed(() => props.errors ?? fieldErrors.value)

const emit = defineEmits(['update:countryCode'])
const model = defineModel()
const phoneRef = ref(null)
const isOpenDropdown = ref(false)
const countries = gon.country_codes
const selectedCountry = ref(findCountry() || countries[0])
const localPhone = ref('')
let isLocalUpdate = false

function findCountry() {
  if (props.countryCode) {
    const found = countries.find(c => c.value === props.countryCode)
    if (found) return found
  }
  if (!model.value) return null
  return countries.find(c => model.value.startsWith(c.number_code))
}

function formatPhone(value, country) {
  const phone = parsePhoneNumber(value, country.value)?.format('NATIONAL', { nationalPrefix: false })
  return (phone || value).replaceAll(' ', '-')
}

function toE164(value, country) {
  const digits = value.replace(/\D/g, '')
  if (!digits) return ''
  const parsed = parsePhoneNumber(digits, country.value)
  return parsed?.format('E.164') || (country.number_code + digits)
}

function validateMaxLength(value) {
  const digits = value.replace(/\W/g, '')
  return digits.length <= selectedCountry.value.number_max
}

function onInput(event) {
  if (fieldErrors.value.length) clearFieldErrors()
  const PHONE_REGEXP_SYMBOLS = /[^()\-+0-9\s]/g
  let raw = event.target.value.replaceAll(PHONE_REGEXP_SYMBOLS, '')
  if (validateMaxLength(raw)) {
    const national = formatPhone(raw, selectedCountry.value)
    event.target.value = national
    localPhone.value = national
    isLocalUpdate = true
    model.value = toE164(national, selectedCountry.value)
    nextTick(() => { isLocalUpdate = false })
  } else {
    event.target.value = localPhone.value
  }
}

function onSelectOption(country) {
  selectedCountry.value = country
  emit('update:countryCode', country.value)
  if (localPhone.value) model.value = toE164(localPhone.value, country)
}

function dropdownOutput(country) {
  return `${country.flag} ${country.text} ${country.number_code}`
}

function syncLocalPhone() {
  if (!model.value) {
    localPhone.value = ''
    return
  }

  const country = findCountry() || selectedCountry.value
  selectedCountry.value = country
  const parsed = parsePhoneNumber(model.value, country.value)
  localPhone.value = parsed ? formatPhone(parsed.nationalNumber, country) : model.value.replace(/\D/g, '')
}

watch(model, () => {
  if (!isLocalUpdate) syncLocalPhone()
  if (fieldErrors.value.length) clearFieldErrors()
}, { immediate: true })
</script>
